import { useState } from "react";

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  TextField,
  Typography
} from "@mui/material";

import ReviewTableShell from "../review-table/ReviewTableShell";
import CsvPager from "./CsvPager";
import CsvLineNotes from "./CsvLineNotes";

import {
  CSV_PAGE_SIZE,
  SHIPPING_MODE_OPTIONS,
  csvCategoryFor,
  csvDescriptionFor,
  csvFactoryCodeFor,
  csvLineKeyFor,
  csvOfficeSubcategoryExtraFor,
  csvProductNameFor,
  csvRouteFor,
  csvStatusFor,
  csvSupplierNameFor,
  csvUsageTypeFor,
  csvVendorNameFor,
  formatCsvDate,
  formatCsvQuantity,
  formatCsvTons,
  shippingModeName,
  vendorBadgeFor
} from "./csvReviewFormat";
import { useCsvLinePages } from "./useCsvReview";

const ALL_GROUPS = "";

const VendorWithBadge = ({ line }) => {
  const { label, color } = vendorBadgeFor(line);
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Typography variant="body2">{csvVendorNameFor(line)}</Typography>
      <Box>
        <Chip size="small" variant="outlined" color={color} label={label} />
      </Box>
    </Box>
  );
};

const StatusChip = ({ line }) => {
  const { label, color } = csvStatusFor(line);
  return <Chip size="small" variant="outlined" color={color} label={label} />;
};

const transactionsColumns = (closedYears) => [
  {
    id: "date",
    label: "Date",
    render: (line) => formatCsvDate(line.period_end_final)
  },
  {
    id: "description",
    label: "Description",
    render: (line) => csvDescriptionFor(line)
  },
  { id: "amount", label: "Amount", render: (line) => formatCsvQuantity(line) },
  {
    id: "vendor",
    label: "Vendor",
    render: (line) => <VendorWithBadge line={line} />
  },
  {
    id: "notes",
    label: "Notes",
    render: (line) => <CsvLineNotes line={line} closedYears={closedYears} />
  },
  {
    id: "status",
    label: "Status",
    render: (line) => <StatusChip line={line} />
  }
];

const shippingColumns = (closedYears) => [
  {
    id: "date",
    label: "Ship date",
    render: (line) => formatCsvDate(line.period_end_final)
  },
  { id: "route", label: "From → To", render: (line) => csvRouteFor(line) },
  { id: "weight", label: "Weight", render: (line) => formatCsvQuantity(line) },
  {
    id: "mode",
    label: "Mode",
    render: (line) => shippingModeName(line.shipping_mode_final)
  },
  {
    id: "notes",
    label: "Notes",
    render: (line) => <CsvLineNotes line={line} closedYears={closedYears} />
  },
  {
    id: "status",
    label: "Status",
    render: (line) => <StatusChip line={line} />
  }
];

const bomColumns = (closedYears) => [
  {
    id: "product",
    label: "Product",
    render: (line) => csvProductNameFor(line)
  },
  {
    id: "supplier",
    label: "Supplier",
    render: (line) => csvSupplierNameFor(line)
  },
  {
    id: "factory",
    label: "Factory",
    render: (line) => csvFactoryCodeFor(line)
  },
  { id: "category", label: "Category", render: (line) => csvCategoryFor(line) },
  {
    id: "notes",
    label: "Notes",
    render: (line) => <CsvLineNotes line={line} closedYears={closedYears} />
  },
  {
    id: "status",
    label: "Status",
    render: (line) => <StatusChip line={line} />
  }
];

// utilities_csv/office_csv (ticket #412): no vendor on these lines at all,
// so the browser shows the one reading (quantity + unit, split apart like
// CsvAttentionGroups' editable columns) and its draft emissions estimate
// instead.
const utilitiesColumns = (closedYears) => [
  {
    id: "date",
    label: "Date",
    render: (line) => formatCsvDate(line.period_end_final)
  },
  {
    id: "usageType",
    label: "Usage type",
    render: (line) => csvUsageTypeFor(line)
  },
  {
    id: "quantity",
    label: "Quantity",
    render: (line) => formatCsvQuantity(line)
  },
  { id: "tons", label: "tCO2e", render: (line) => formatCsvTons(line.draft_tons_co2e) },
  {
    id: "notes",
    label: "Notes",
    render: (line) => <CsvLineNotes line={line} closedYears={closedYears} />
  },
  {
    id: "status",
    label: "Status",
    render: (line) => <StatusChip line={line} />
  }
];

const officeColumns = (closedYears) => [
  {
    id: "date",
    label: "Date",
    render: (line) => formatCsvDate(line.period_end_final)
  },
  {
    id: "subcategory",
    label: "Subcategory",
    render: (line) => line.subcategory_final || "—"
  },
  {
    id: "quantity",
    label: "Quantity",
    render: (line) => formatCsvQuantity(line)
  },
  {
    id: "subcategoryExtra",
    label: "Details",
    render: (line) => csvOfficeSubcategoryExtraFor(line)
  },
  { id: "tons", label: "tCO2e", render: (line) => formatCsvTons(line.draft_tons_co2e) },
  {
    id: "notes",
    label: "Notes",
    render: (line) => <CsvLineNotes line={line} closedYears={closedYears} />
  },
  {
    id: "status",
    label: "Status",
    render: (line) => <StatusChip line={line} />
  }
];

// One config per family instead of a growing isShipping-style boolean
// (ticket DlAT2n1ITtjB6BA9ntuJ) — a fourth family adds a fourth entry here.
const BROWSER_COLUMNS_BY_FAMILY = {
  shipping_transactions: shippingColumns,
  bom_products_csv: bomColumns,
  utilities_csv: utilitiesColumns,
  office_csv: officeColumns
};

const csvBrowserColumns = ({ documentFamily, closedYears }) =>
  (BROWSER_COLUMNS_BY_FAMILY[documentFamily] || transactionsColumns)(
    closedYears
  );

const GROUP_FIELD_LABELS = {
  shipping_transactions: "Service type",
  bom_products_csv: "Supplier",
  utilities_csv: "Usage type",
  office_csv: "Usage type"
};

const groupFieldLabelFor = (documentFamily) =>
  GROUP_FIELD_LABELS[documentFamily] || "Vendor";

// One shipping-mode decision per service type (csv-update-line-group with
// the breakdown's service groupKey), since a carrier's service names map to
// a mode the same way on every row.
const ServiceModePanel = ({ serviceGroups, onGroupUpdate }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 1 }}>
    <Typography variant="subtitle2">Shipping mode by service type</Typography>
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
      {serviceGroups.map(({ groupKey, label, line_count: lineCount }) => (
        <TextField
          key={groupKey}
          select
          size="small"
          label={`${label || "No service type"} (${lineCount})`}
          value=""
          onChange={({ target: { value } }) =>
            value && onGroupUpdate(groupKey, { shipping_mode_final: value })
          }
          SelectProps={{ native: true }}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 200 }}
        >
          <option value="">Set mode for these shipments</option>
          {SHIPPING_MODE_OPTIONS.map(({ value, name }) => (
            <option key={value} value={value}>
              {name}
            </option>
          ))}
        </TextField>
      ))}
    </Box>
  </Box>
);

const FILTER_LABELS = { ready: "Ready", committed: "Recorded" };

// The "Ready" browser: every line nobody has to decide on, 50 at a time, so
// a reviewer can spot-check before "Confirm N ready lines". Also pages the
// recorded lines once some are committed.
const CsvLinesBrowser = ({
  companyId,
  taskDocId,
  documentFamily,
  summary,
  closedYears,
  refreshKey,
  canReview,
  isEnabled,
  onGroupUpdate
}) => {
  const [filter, setFilter] = useState("ready");
  const [groupKey, setGroupKey] = useState(ALL_GROUPS);
  const { readyCount = 0, committedCount = 0, breakdown = [] } = summary || {};

  const pages = useCsvLinePages({
    companyId,
    taskDocId,
    filter,
    groupKey: groupKey || undefined,
    isEnabled,
    refreshKey
  });

  const counts = { ready: readyCount, committed: committedCount };
  const serviceGroups = breakdown.filter(({ kind }) => kind === "service");

  const controls = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 1 }}>
      {documentFamily === "shipping_transactions" &&
        canReview &&
        !!serviceGroups.length && (
          <ServiceModePanel
            serviceGroups={serviceGroups}
            onGroupUpdate={onGroupUpdate}
          />
        )}
      <Box
        sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}
      >
        {Object.entries(FILTER_LABELS).map(([value, label]) => (
          <Button
            key={value}
            size="small"
            variant={filter === value ? "contained" : "outlined"}
            onClick={() => setFilter(value)}
          >
            {label} ({counts[value]})
          </Button>
        ))}
        {!!breakdown.length && (
          <TextField
            select
            size="small"
            label={groupFieldLabelFor(documentFamily)}
            value={groupKey}
            onChange={({ target: { value } }) => setGroupKey(value)}
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 220 }}
          >
            <option value={ALL_GROUPS}>All</option>
            {breakdown.map(
              ({ groupKey: key, label, line_count: lineCount }) => (
                <option key={key} value={key}>
                  {label || "Unnamed"} ({lineCount})
                </option>
              )
            )}
          </TextField>
        )}
        <CsvPager pages={pages} pageSize={CSV_PAGE_SIZE} />
      </Box>
      {pages.error && <Alert severity="error">{pages.error}</Alert>}
    </Box>
  );

  const emptyBody =
    !pages.lines.length &&
    (pages.isLoading ? (
      <CircularProgress size={20} />
    ) : (
      <Typography variant="body2" color="textSecondary">
        No {FILTER_LABELS[filter].toLowerCase()} lines here.
      </Typography>
    ));

  return (
    <ReviewTableShell
      title={filter === "ready" ? "Ready to confirm" : "Recorded"}
      subtitle={`${counts[filter]} ${counts[filter] === 1 ? "line" : "lines"}`}
      defaultExpanded
      banners={controls}
      columns={csvBrowserColumns({ documentFamily, closedYears })}
      lines={pages.lines}
      getLineKey={csvLineKeyFor}
      body={emptyBody || undefined}
    />
  );
};

export default CsvLinesBrowser;
